import {
  createColumnHelper,
  getCoreRowModel,
  type PaginationState,
  useReactTable,
} from '@tanstack/react-table';
import { Calendar, Loader2Icon } from 'lucide-react';
import { useEffect, useState } from 'react';

import {
  type NoAttendanceStudent,
  useGetActiveStudentsNoAttendance,
} from '~entities/dashboard/api/get-active-students-no-attendance';

import { DataTablePagination } from '~shared/components/table/data-table-pagination';
import { getStoredPageSize } from '~shared/helpers/page-size-preference';
import { Avatar, AvatarFallback } from '~shared/shadcn/ui/avatar';
import { Badge } from '~shared/shadcn/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';

interface NoAttendanceTabProps {
  sessionId: string;
}

const columnHelper = createColumnHelper<NoAttendanceStudent>();
const paginationColumns = [columnHelper.display({ id: 'cards' })];

export const NoAttendanceTab = ({ sessionId }: NoAttendanceTabProps) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: getStoredPageSize(),
  });

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [sessionId]);

  const { data: noAttendanceStudents, isFetching: isFetchingNoAttendance } =
    useGetActiveStudentsNoAttendance({
      sessionId,
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
    });

  const table = useReactTable({
    data: noAttendanceStudents?.data ?? [],
    columns: paginationColumns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    rowCount: noAttendanceStudents?.pagination.total ?? 0,
    onPaginationChange: setPagination,
    state: {
      pagination,
    },
  });

  return (
    <div className='space-y-4'>
      {isFetchingNoAttendance ? (
        <div className='flex items-center justify-center'>
          <Loader2Icon className='size-8 animate-spin' />
        </div>
      ) : (
        <>
          <div className='space-y-2'>
            {!noAttendanceStudents?.data ||
            noAttendanceStudents.data.length === 0 ? (
              <div className='flex items-center justify-center'>
                <p className='text-sm text-muted-foreground'>
                  No students found
                </p>
              </div>
            ) : (
              noAttendanceStudents.data.map((item) => (
                <div
                  key={item.studentId}
                  className='flex min-w-0 flex-col gap-3 rounded-xl border border-border/50 p-3.5 transition-all duration-150 hover:-translate-y-px hover:bg-muted/50 hover:shadow-md md:flex-row md:items-center md:justify-between'
                >
                  <div className='flex min-w-0 shrink-0 items-center gap-2 md:max-w-[40%]'>
                    <Avatar className='shrink-0'>
                      <AvatarFallback className='bg-primary font-semibold text-primary-foreground'>
                        {item.studentName
                          .split(' ')
                          .map((name) => name.charAt(0))
                          .join('')
                          .toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className='min-w-0 truncate text-sm font-semibold text-secondary'>
                      {item.studentName}
                    </div>
                  </div>
                  <div className='flex min-w-0 flex-wrap items-center gap-2 md:flex-1 md:justify-end'>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <Badge
                            variant='outline'
                            className='items-center gap-1 border-purple-200 bg-purple-50 text-xs font-medium text-purple-700'
                          >
                            <Calendar className='size-3' />
                            Classes: {item.activeClasses}
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent className='border bg-background'>
                          <div className='space-y-1 text-xs'>
                            {item.activeClassNames.map((className, index) => (
                              <div key={`${className}-${index}`}>
                                {className}
                              </div>
                            ))}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <Badge
                      variant='outline'
                      className='items-center gap-1 border-blue-200 bg-blue-50 text-xs font-medium text-blue-700'
                    >
                      <Calendar className='size-3' />
                      Past Lessons: {item.totalClasses}
                    </Badge>
                    <Badge
                      variant='outline'
                      className='items-center gap-1 border-green-300 bg-green-100 text-green-700'
                    >
                      Present: {item.presentCount}
                    </Badge>
                    <Badge
                      variant='outline'
                      className='items-center gap-1 border-red-300 bg-red-100 text-red-700'
                    >
                      Absent: {item.absentCount}
                    </Badge>
                    <Badge
                      variant='outline'
                      className='items-center gap-1 border-yellow-300 bg-yellow-100 text-yellow-700'
                    >
                      Missing: {item.missingCount}
                    </Badge>
                  </div>
                </div>
              ))
            )}
          </div>

          {noAttendanceStudents?.pagination && (
            <div className='pt-2'>
              <DataTablePagination table={table} />
            </div>
          )}
        </>
      )}
    </div>
  );
};
